import type { RemoteFakeResolver } from "./remote-fakes"
import {
  createRemoteCommandOutcome,
  RemoteCommandFailure,
  type AnyRemoteCommandEventDefinition,
  type RemoteCommand,
  type RemoteCommandEventDefinitions,
  type RemoteCommandOutcome,
  type RemoteCommandResult,
} from "./remote-command"
import type { PresenterResolver } from "../presentation/presenter"
import type {
  ApplicationTelemetryRuntime,
  RemoteTelemetryRequest,
  RemoteTelemetryOperation,
} from "../telemetry/telemetry"
import type { Messenger } from "../messaging/messenger"
import { readRemoteFile, type RemoteFile } from "./remote-file"

type MaybePromise<T> = T | Promise<T>

export type RemoteHeadersProvider = HeadersInit | (() => MaybePromise<HeadersInit>)

export interface BasicCredentials {
  username: string
  password: string
}

export type AuthorizationChangeSource = "configured" | "response"

export interface AuthorizationChangedEvent {
  authorization?: string
  source: AuthorizationChangeSource
}

export interface AuthenticationRequiredEvent {
  status: 401
  url: string
}

export interface RemoteNetworkConnectionOptions {
  /** Requests without a matching fake go to the server (the default). */
  mode?: "network"
  baseUrl: string
  credentials?: RequestCredentials
  fetch?: typeof globalThis.fetch
  headers?: RemoteHeadersProvider
}

/**
 * Fakes are the only source: a query or command without a matching fake fails at once with a
 * `RemoteUnhandledFakeError`, and no request reaches the network. `baseUrl` is optional and only
 * identifies the connection (for example in the session access context).
 */
export interface RemoteFakesOnlyConnectionOptions {
  mode: "fakes-only"
  baseUrl?: string
  credentials?: never
  fetch?: never
  headers?: never
}

export type RemoteConnectionOptions = RemoteNetworkConnectionOptions | RemoteFakesOnlyConnectionOptions

export type RemoteClientOptions = RemoteConnectionOptions &
  (
    | {
        authorization?: string
        basicCredentials?: never
      }
    | {
        authorization?: never
        basicCredentials: BasicCredentials
      }
  )

export interface GraphQLResponseError {
  message: string
  path?: readonly (string | number)[]
  extensions?: Record<string, unknown>
}

interface GraphQLResponse<TData> {
  data?: TData
  errors?: GraphQLResponseError[]
}

export const CommandMessageKind = {
  Result: 0,
  Request: 1,
  Notify: 2,
  State: 3,
} as const

export type CommandMessageKindValue = (typeof CommandMessageKind)[keyof typeof CommandMessageKind]

export interface RemoteMessage<TValue = unknown> {
  kind: CommandMessageKindValue
  type?: string
  namespace?: string
  value?: TValue
}

export interface CommandExecutionOptions {
  signal?: AbortSignal
  onNotify?: (message: RemoteMessage) => MaybePromise<void>
  onRequest?: (message: RemoteMessage) => MaybePromise<unknown>
}

export class RemoteError extends Error {
  constructor(message: string, options?: ErrorOptions) {
    super(message, options)
    this.name = "RemoteError"
  }
}

export class RemoteHttpError extends RemoteError {
  constructor(
    public readonly status: number,
    public readonly statusText: string,
    public readonly url: string,
    public readonly responseBody: string
  ) {
    super(`Remote request failed with HTTP ${status} ${statusText}.`)
    this.name = "RemoteHttpError"
  }
}

export class RemoteGraphQLError extends RemoteError {
  constructor(
    public readonly errors: readonly GraphQLResponseError[],
    public readonly data?: unknown
  ) {
    super(errors.map((error) => error.message).join("\n") || "GraphQL request failed.")
    this.name = "RemoteGraphQLError"
  }
}

export class RemoteProtocolError extends RemoteError {
  constructor(message: string, options?: ErrorOptions) {
    super(message, options)
    this.name = "RemoteProtocolError"
  }
}

/** A query or command had no matching fake while the remote connection is in fakes-only mode. */
export class RemoteUnhandledFakeError extends RemoteError {
  constructor(
    public readonly kind: "query" | "command",
    /** The GraphQL endpoint, or the command route. */
    public readonly target: string,
    /** The full GraphQL document text; absent for commands. */
    public readonly document?: string
  ) {
    super(
      kind === "query"
        ? `No fake handles the query on endpoint "${target}" (document ${describeGraphQLDocument(document ?? "")}), and the remote connection is fakes-only.`
        : `No fake handles the command route "${target}", and the remote connection is fakes-only.`
    )
    this.name = "RemoteUnhandledFakeError"
  }
}

export class RemoteAccessContextChangedError extends RemoteError {
  constructor() {
    super("The remote access context changed while the request was in progress.")
    this.name = "RemoteAccessContextChangedError"
  }
}

export class RemoteInteractionRequiredError extends RemoteProtocolError {
  constructor(
    public readonly interactionId: string | null,
    public readonly request: RemoteMessage
  ) {
    super(
      `Remote command requested client interaction${interactionId ? ` (${interactionId})` : ""}, but no presenter or onRequest handler handled it.`
    )
    this.name = "RemoteInteractionRequiredError"
  }
}

interface RemoteTransportResponse {
  readonly response: Response
  readonly signal: AbortSignal
  readonly telemetry?: RemoteTelemetryRequest
}

interface RemoteTransportTelemetryOptions {
  readonly kind: "query" | "command"
  readonly name: string
}

class RemoteTransport {
  private baseUrl: string
  readonly credentials: RequestCredentials
  private authorization?: string
  /**
   * Set after an HTTP 401: the authorization is withheld without starting a new access context, so the
   * same user can resume it. Any configured change or server change ends the suspension with a new context.
   */
  private authorizationSuspended = false
  /** Advances when a suspended authorization resumes; a 401 to a request sent before then is stale. */
  private authorizationEpoch = 0
  private revision = 0
  private accessAbortController = new AbortController()
  private readonly authorizationListeners = new Set<(event: AuthorizationChangedEvent) => void>()
  private readonly authenticationRequiredListeners = new Set<(event: AuthenticationRequiredEvent) => void>()
  private readonly revisionListeners = new Set<() => void>()
  private readonly resumeListeners = new Set<() => void>()
  private readonly fetchImpl?: typeof globalThis.fetch
  private readonly headersProvider?: RemoteHeadersProvider
  readonly fakesOnly: boolean

  constructor(
    options: RemoteClientOptions,
    private readonly telemetry?: ApplicationTelemetryRuntime
  ) {
    this.fakesOnly = options.mode === "fakes-only"
    this.baseUrl = normalizeBaseUrl(options.baseUrl ?? "")
    this.authorization =
      options.basicCredentials == null
        ? options.authorization
        : createBasicAuthorization(options.basicCredentials.username, options.basicCredentials.password)
    this.credentials = options.credentials ?? "include"
    this.headersProvider = options.headers

    if (!this.fakesOnly) {
      const fetchImpl = options.fetch ?? globalThis.fetch
      if (fetchImpl == null) {
        throw new RemoteError("No Fetch API implementation is available.")
      }
      this.fetchImpl = options.fetch ?? fetchImpl.bind(globalThis)
    }
  }

  setAuthorization(authorization?: string) {
    this.updateAuthorization(authorization, "configured")
  }

  getAuthorization() {
    return this.authorization
  }

  setBasicCredentials(username: string, password: string) {
    this.updateAuthorization(createBasicAuthorization(username, password), "configured")
  }

  subscribeAuthorization(listener: (event: AuthorizationChangedEvent) => void) {
    this.authorizationListeners.add(listener)
    return () => this.authorizationListeners.delete(listener)
  }

  subscribeAuthenticationRequired(listener: (event: AuthenticationRequiredEvent) => void) {
    this.authenticationRequiredListeners.add(listener)
    return () => this.authenticationRequiredListeners.delete(listener)
  }

  getRevision() {
    return this.revision
  }

  isAuthorizationSuspended() {
    return this.authorizationSuspended
  }

  /** Withholds the authorization after a 401 without changing the access context. */
  suspendAuthorization() {
    if (this.authorizationSuspended) return
    this.authorizationSuspended = true
    if (this.authorization == null) return
    this.authorization = undefined
    const event: AuthorizationChangedEvent = { authorization: undefined, source: "configured" }
    for (const listener of this.authorizationListeners) listener(event)
  }

  /**
   * Restores a suspended access context with a new authorization for the same user and server.
   * Returns false, and changes nothing, when the authorization is not suspended.
   */
  resumeAuthorization(authorization: string) {
    if (!this.authorizationSuspended) return false
    this.authorizationSuspended = false
    this.authorizationEpoch += 1
    this.authorization = authorization
    const event: AuthorizationChangedEvent = { authorization, source: "configured" }
    for (const listener of this.authorizationListeners) listener(event)
    for (const listener of this.resumeListeners) listener()
    return true
  }

  subscribeAuthorizationResumed(listener: () => void) {
    this.resumeListeners.add(listener)
    return () => {
      this.resumeListeners.delete(listener)
    }
  }

  subscribeRevision(listener: () => void) {
    this.revisionListeners.add(listener)
    return () => this.revisionListeners.delete(listener)
  }

  setBaseUrl(baseUrl: string) {
    const normalized = normalizeBaseUrl(baseUrl)
    if (this.baseUrl === normalized) return

    this.baseUrl = normalized
    this.authorizationSuspended = false
    if (this.authorization == null) this.bumpRevision()
    else this.updateAuthorization(undefined, "configured")
  }

  getBaseUrl() {
    return this.baseUrl
  }

  async send(
    path: string,
    init: RequestInit,
    defaults?: HeadersInit,
    telemetryOptions?: RemoteTransportTelemetryOptions
  ) {
    if (this.fetchImpl == null) {
      throw new RemoteError("The remote connection is fakes-only and has no transport.")
    }
    const requestBaseUrl = this.baseUrl
    const requestAuthorization = this.authorization
    const requestAuthorizationEpoch = this.authorizationEpoch
    const requestSignal =
      init.signal == null
        ? this.accessAbortController.signal
        : AbortSignal.any([init.signal, this.accessAbortController.signal])
    const headers = new Headers(defaults)
    const configuredHeaders =
      typeof this.headersProvider === "function" ? await this.headersProvider() : this.headersProvider

    requestSignal.throwIfAborted()

    if (configuredHeaders != null) {
      new Headers(configuredHeaders).forEach((value, key) => headers.set(key, value))
    }
    if (requestAuthorization != null && !headers.has("Authorization")) {
      headers.set("Authorization", requestAuthorization)
    }
    if (init.headers != null) {
      new Headers(init.headers).forEach((value, key) => headers.set(key, value))
    }

    const url = resolveUrl(requestBaseUrl, path)
    const requestTelemetry =
      telemetryOptions == null
        ? undefined
        : this.telemetry?.startRemoteRequest(
            telemetryOptions.kind,
            telemetryOptions.name,
            init.method ?? "GET",
            headers
          )
    try {
      const response = await this.fetchImpl(url, {
        ...init,
        credentials: this.credentials,
        headers,
        signal: requestSignal,
      })

      requestSignal.throwIfAborted()

      // The current .NET client also accepts an updated Authorization value from
      // a response. Browsers can only observe this header when CORS exposes it.
      if (response.headers.has("Authorization")) {
        this.updateAuthorization(response.headers.get("Authorization") || undefined, "response")
      }
      requestSignal.throwIfAborted()

      if (!response.ok) {
        let responseBody = ""
        try {
          responseBody = await response.text()
        } catch {
          // Preserve the HTTP failure even if the body cannot be read.
        }
        requestSignal.throwIfAborted()
        // A 401 to a request sent before a resumed login belongs to the expired authorization.
        if (response.status === 401 && requestAuthorizationEpoch === this.authorizationEpoch) {
          const event: AuthenticationRequiredEvent = { status: 401, url }
          for (const listener of this.authenticationRequiredListeners) listener(event)
        }
        requestTelemetry?.end("error", response.status)
        throw new RemoteHttpError(response.status, response.statusText, url, responseBody)
      }

      return {
        response,
        signal: requestSignal,
        telemetry: requestTelemetry,
      } satisfies RemoteTransportResponse
    } catch (error) {
      requestTelemetry?.end(isRemoteCancellation(error) ? "cancelled" : "error")
      throw error
    }
  }

  private updateAuthorization(authorization: string | undefined, source: AuthorizationChangeSource) {
    // The identity behind a response Authorization is unknown while suspended; only a login resumes.
    if (source === "response" && this.authorizationSuspended) return
    const endsSuspension = this.authorizationSuspended
    if (this.authorization === authorization && !endsSuspension) return

    this.authorizationSuspended = false
    const changed = this.authorization !== authorization
    this.authorization = authorization
    if (changed) {
      const event: AuthorizationChangedEvent = { authorization, source }
      for (const listener of this.authorizationListeners) listener(event)
    }
    if (source === "configured" || authorization == null) this.bumpRevision()
  }

  private bumpRevision() {
    const previousContext = this.accessAbortController
    this.accessAbortController = new AbortController()
    this.revision += 1
    previousContext.abort(new RemoteAccessContextChangedError())
    for (const listener of this.revisionListeners) listener()
  }
}

function describeGraphQLDocument(document: string) {
  const name = /\b(?:query|mutation|subscription)\s+([A-Za-z_]\w*)/.exec(document)?.[1]
  if (name != null) return `"${name}"`
  const text = document.replace(/\s+/g, " ").trim()
  return JSON.stringify(text.length > 80 ? `${text.slice(0, 80)}...` : text)
}

function normalizeBaseUrl(baseUrl: string) {
  return baseUrl.replace(/\/+$/, "")
}

function resolveUrl(baseUrl: string, path: string) {
  return `${baseUrl}/${path.replace(/^\/+/, "")}`
}

/** @internal */
export function createBasicAuthorization(username: string, password: string) {
  if (username.length === 0) return undefined

  const bytes = new TextEncoder().encode(`${username}:${password}`)
  let binary = ""
  for (const byte of bytes) binary += String.fromCharCode(byte)
  return `Basic ${btoa(binary)}`
}

export interface GraphQLClient {
  query<TData, TVariables = Record<string, unknown>>(
    endpoint: string,
    query: string,
    variables?: TVariables,
    signal?: AbortSignal
  ): Promise<TData>
}

class DefaultGraphQLClient implements GraphQLClient {
  constructor(
    private readonly transport: RemoteTransport,
    private readonly fakeResolver?: RemoteFakeResolver,
    private readonly scopeIds: readonly string[] = [],
    private readonly telemetry?: ApplicationTelemetryRuntime
  ) {}

  async query<TData, TVariables = Record<string, unknown>>(
    endpoint: string,
    query: string,
    variables?: TVariables,
    signal?: AbortSignal
  ): Promise<TData> {
    if (this.fakeResolver != null) {
      const startedAt = this.telemetry?.telemetry.debugEnabled === true ? performanceNow() : undefined
      const resolution = this.fakeResolver.resolveQuery<TData, Extract<TVariables, object>>(
        this.scopeIds,
        endpoint,
        query,
        variables as Extract<TVariables, object> | undefined,
        signal
      )
      const fake = resolution instanceof Promise ? await resolution : resolution
      if (fake.handled) {
        if (startedAt != null) {
          logFakeExecution(this.telemetry, "query", endpoint, performanceNow() - startedAt, variables, fake.value)
        }
        return fake.value
      }
    }

    const operation = this.telemetry?.startRemoteOperation("query", endpoint)
    try {
      if (this.transport.fakesOnly) throw new RemoteUnhandledFakeError("query", endpoint, query)
      const {
        response,
        signal: requestSignal,
        telemetry: requestTelemetry,
      } = await this.transport.send(
        `graphql/${endpoint}`,
        {
          method: "POST",
          body: JSON.stringify({ query, variables }),
          signal,
        },
        {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        { kind: "query", name: endpoint }
      )
      let payload: GraphQLResponse<TData>
      try {
        payload = (await response.json()) as GraphQLResponse<TData>
        requestTelemetry?.end("success", response.status)
      } catch (cause) {
        requestTelemetry?.end("error", response.status)
        requestSignal?.throwIfAborted()
        throw new RemoteProtocolError("GraphQL endpoint returned invalid JSON.", { cause })
      }
      requestSignal?.throwIfAborted()

      if (payload.errors?.length) {
        throw new RemoteGraphQLError(payload.errors, payload.data)
      }
      if (!("data" in payload)) {
        throw new RemoteProtocolError("GraphQL response did not contain a data property.")
      }

      operation?.end("success")
      return payload.data as TData
    } catch (error) {
      operation?.end(isRemoteCancellation(error) ? "cancelled" : "error", error)
      throw error
    }
  }
}

export interface CommandClient {
  /**
   * The raw low-level call: resolves to whatever the server answered (a RemoteCommandResult, a direct result
   * or a RemoteFile), also for a failed command. Declared events are emitted to the messenger.
   */
  execute<TInput, TResult, TEvents extends RemoteCommandEventDefinitions | undefined>(
    command: RemoteCommand<TInput, TResult, TEvents>,
    options?: CommandExecutionOptions
  ): Promise<TResult>
  /**
   * Executes a command that answers with a RemoteCommandResult, like `execute` (declared events are emitted
   * to the messenger first). A result with `isSucceeded: false` throws a RemoteCommandFailure; otherwise the
   * outcome carries the `value` and one typed property per declared event.
   */
  run<TInput, TValue, TEvents extends RemoteCommandEventDefinitions | undefined>(
    command: RemoteCommand<TInput, RemoteCommandResult<TValue>, TEvents>,
    options?: CommandExecutionOptions
  ): Promise<RemoteCommandOutcome<TValue, TEvents>>
}

class DefaultCommandClient implements CommandClient {
  private static readonly interactionMediaType = "application/vnd+florisoft.message+json"
  private static readonly accept = `${DefaultCommandClient.interactionMediaType}+1, application/json`
  private readonly decoder = new TextDecoder()

  constructor(
    private readonly transport: RemoteTransport,
    private readonly fakeResolver?: RemoteFakeResolver,
    private readonly scopeIds: readonly string[] = [],
    private readonly presenterResolver?: PresenterResolver,
    private readonly telemetry?: ApplicationTelemetryRuntime,
    private readonly messenger?: Messenger
  ) {}

  async execute<TInput, TResult, TEvents extends RemoteCommandEventDefinitions | undefined>(
    command: RemoteCommand<TInput, TResult, TEvents>,
    options: CommandExecutionOptions = {}
  ): Promise<TResult> {
    const result = await this.executeCore(command, options)
    this.emitCommandEvents(command, result)
    return result
  }

  async run<TInput, TValue, TEvents extends RemoteCommandEventDefinitions | undefined>(
    command: RemoteCommand<TInput, RemoteCommandResult<TValue>, TEvents>,
    options: CommandExecutionOptions = {}
  ): Promise<RemoteCommandOutcome<TValue, TEvents>> {
    const result = await this.execute(command, options)
    if (!result.isSucceeded) throw new RemoteCommandFailure(command.route, result)
    return createRemoteCommandOutcome(command, result)
  }

  private async executeCore<TInput, TResult, TEvents extends RemoteCommandEventDefinitions | undefined>(
    command: RemoteCommand<TInput, TResult, TEvents>,
    options: CommandExecutionOptions
  ): Promise<TResult> {
    const { route, input } = command
    if (this.fakeResolver != null) {
      const startedAt = this.telemetry?.telemetry.debugEnabled === true ? performanceNow() : undefined
      const resolution = this.fakeResolver.resolveCommand<TResult, TInput>(this.scopeIds, route, input, options.signal)
      const fake = resolution instanceof Promise ? await resolution : resolution
      if (fake.handled) {
        if (startedAt != null) {
          logFakeExecution(this.telemetry, "command", route, performanceNow() - startedAt, input, fake.value)
        }
        return this.freezeCommandEvents(fake.value)
      }
    }

    const operation = this.telemetry?.startRemoteOperation("command", route)
    try {
      if (this.transport.fakesOnly) throw new RemoteUnhandledFakeError("command", route)
      const result = await this.sendCommand(`api/${route}`, JSON.stringify(input), options.signal, route)
      const value = await this.readResult<TResult>(result, options, operation, route, command.result === "file")
      operation?.end("success")
      return this.freezeCommandEvents(value)
    } catch (error) {
      operation?.end(isRemoteCancellation(error) ? "cancelled" : "error", error)
      throw error
    }
  }

  private emitCommandEvents<TInput, TResult, TEvents extends RemoteCommandEventDefinitions | undefined>(
    command: RemoteCommand<TInput, TResult, TEvents>,
    result: TResult
  ) {
    const definitions: RemoteCommandEventDefinitions | undefined = command.events
    if (definitions == null || this.messenger == null) return
    if (result == null || typeof result !== "object") return

    const events = (result as { readonly events?: unknown }).events
    if (!Array.isArray(events)) return

    for (const event of events) {
      if (event == null || typeof event !== "object") continue
      const type = (event as { readonly $type?: unknown }).$type
      if (typeof type !== "string") continue

      for (const definition of Object.values(definitions)) {
        if (definition.type !== type) continue
        this.emitCommandEvent(command.route, definition, event)
        break
      }
    }
  }

  private emitCommandEvent(route: string, definition: AnyRemoteCommandEventDefinition, event: object) {
    if (this.messenger == null) return
    try {
      this.messenger.emit(definition, event)
    } catch (error) {
      this.reportCommandEventDeliveryError(
        "Messenger subscriber failed while handling a remote command event.",
        "remote-command-messenger",
        route,
        definition.type,
        error
      )
    }
  }

  private reportCommandEventDeliveryError(
    message: string,
    source: string,
    route: string,
    messageType: string,
    error: unknown
  ) {
    const attributes = {
      "florisoft.error.source": source,
      "florisoft.remote.kind": "command",
      "florisoft.remote.name": route,
      "florisoft.message.type": messageType,
    } as const
    if (this.telemetry == null) {
      console.error(message, error)
    } else {
      this.telemetry.telemetry.error(message, error, attributes)
    }
  }

  private freezeCommandEvents<TResult>(result: TResult): TResult {
    if (result == null || typeof result !== "object") return result
    const events = (result as { readonly events?: unknown }).events
    if (!Array.isArray(events)) return result

    for (const event of events) {
      if (event != null && typeof event === "object") Object.freeze(event)
    }
    return result
  }

  private sendCommand(path: string, body: string, signal: AbortSignal | undefined, telemetryName: string) {
    return this.transport.send(
      path,
      {
        method: "POST",
        body,
        signal,
      },
      {
        Accept: DefaultCommandClient.accept,
        "Content-Type": "application/json",
      },
      { kind: "command", name: telemetryName }
    )
  }

  private async readResult<TResult>(
    result: RemoteTransportResponse,
    options: CommandExecutionOptions,
    telemetryOperation?: RemoteTelemetryOperation,
    telemetryName = "command",
    file = false
  ): Promise<TResult> {
    const { response, signal } = result
    try {
      signal?.throwIfAborted()
    } catch (error) {
      result.telemetry?.end("cancelled", response.status)
      throw error
    }
    const contentType = response.headers.get("Content-Type")?.toLowerCase() ?? ""

    if (contentType.startsWith(DefaultCommandClient.interactionMediaType)) {
      return this.readInteractionResult<TResult>(
        result,
        { ...options, signal },
        telemetryOperation,
        telemetryName,
        file
      )
    }

    // The server writes a file result as the raw file with `Content-Disposition: attachment`, so a file can have any content type.
    // Without that header, a JSON answer is the envelope of a command that returned no file.
    if (file && (response.headers.has("Content-Disposition") || !contentType.includes("application/json"))) {
      let remoteFile: RemoteFile
      try {
        remoteFile = await readRemoteFile(response)
        result.telemetry?.end("success", response.status)
      } catch (error) {
        result.telemetry?.end(isRemoteCancellation(error) ? "cancelled" : "error", response.status)
        throw error
      }
      signal?.throwIfAborted()
      return remoteFile as TResult
    }

    if (contentType.includes("application/json")) {
      if (file) {
        result.telemetry?.end("success", response.status)
        throw new RemoteProtocolError("File command returned JSON instead of a file.")
      }
      let text: string
      try {
        text = await response.text()
        result.telemetry?.end("success", response.status)
      } catch (error) {
        result.telemetry?.end(isRemoteCancellation(error) ? "cancelled" : "error", response.status)
        throw error
      }
      signal?.throwIfAborted()
      if (text.length === 0) {
        return undefined as TResult
      }
      try {
        return JSON.parse(text) as TResult
      } catch (cause) {
        throw new RemoteProtocolError("Command endpoint returned invalid JSON.", { cause })
      }
    }

    result.telemetry?.end("success", response.status)
    throw new RemoteProtocolError(`Unsupported command response content type: ${contentType || "<none>"}.`)
  }

  private async readInteractionResult<TResult>(
    result: RemoteTransportResponse,
    options: CommandExecutionOptions,
    telemetryOperation?: RemoteTelemetryOperation,
    telemetryName = "command",
    file = false
  ): Promise<TResult> {
    const { response } = result
    let bytes: Uint8Array
    try {
      bytes = new Uint8Array(await response.arrayBuffer())
      result.telemetry?.end("success", response.status)
    } catch (error) {
      result.telemetry?.end(isRemoteCancellation(error) ? "cancelled" : "error", response.status)
      throw error
    }
    options.signal?.throwIfAborted()
    let offset = 0

    while (offset < bytes.length) {
      options.signal?.throwIfAborted()
      if (bytes.length - offset < 10) {
        throw new RemoteProtocolError("Florisoft command frame is missing its 10-byte length header.")
      }

      const lengthText = this.decoder.decode(bytes.subarray(offset, offset + 10))
      offset += 10
      if (!/^\d{10}$/.test(lengthText)) {
        throw new RemoteProtocolError(`Invalid Florisoft command frame length: ${lengthText}.`)
      }

      const payloadLength = Number.parseInt(lengthText, 10)
      if (payloadLength <= 0 || offset + payloadLength > bytes.length) {
        throw new RemoteProtocolError(`Invalid Florisoft command frame payload length: ${payloadLength}.`)
      }

      const payloadText = this.decoder.decode(bytes.subarray(offset, offset + payloadLength))
      offset += payloadLength

      let message: RemoteMessage
      try {
        message = JSON.parse(payloadText) as RemoteMessage
      } catch (cause) {
        throw new RemoteProtocolError("Florisoft command frame contained invalid JSON.", { cause })
      }

      switch (message.kind) {
        case CommandMessageKind.Result:
          options.signal?.throwIfAborted()
          return message.value as TResult

        case CommandMessageKind.Notify:
          if (options.onNotify != null) {
            await options.onNotify(message)
            options.signal?.throwIfAborted()
          } else {
            void this.tryPresent(message, options.signal, "notify").catch((error) => {
              if (this.presenterResolver != null) {
                this.presenterResolver.reportNotificationError(error, message.type!, message.namespace, options.signal)
                return
              }
              if (options.signal?.aborted) return
              if (this.telemetry == null) {
                console.error("Presenter failed while handling a remote Notify message.", error)
              } else {
                this.telemetry.telemetry.error("Presenter failed while handling a remote Notify message.", error, {
                  "florisoft.error.source": "remote-presenter",
                })
              }
            })
          }
          break

        case CommandMessageKind.Request: {
          const interactionId = response.headers.get("InteractionId")
          if (interactionId == null || interactionId.length === 0) {
            throw new RemoteProtocolError(
              "Remote command requested interaction but did not return an InteractionId header."
            )
          }

          let responseValue: unknown
          const interactionStartedAt = performanceNow()
          try {
            if (options.onRequest != null) {
              responseValue = await options.onRequest(message)
            } else {
              const presentation = await this.tryPresent(message, options.signal)
              if (!presentation.handled) {
                throw new RemoteInteractionRequiredError(interactionId, message)
              }
              responseValue = presentation.value
            }
          } finally {
            telemetryOperation?.excludeDuration(performanceNow() - interactionStartedAt)
          }
          options.signal?.throwIfAborted()
          const continuation = await this.sendCommand(
            `api/Interaction/${encodeURIComponent(interactionId)}`,
            responseValue === undefined ? "" : JSON.stringify(responseValue),
            options.signal,
            telemetryName
          )
          return this.readResult<TResult>(continuation, options, telemetryOperation, telemetryName, file)
        }

        case CommandMessageKind.State:
          throw new RemoteProtocolError("Florisoft command State messages are not supported.")

        default:
          throw new RemoteProtocolError(`Unknown Florisoft command message kind: ${String(message.kind)}.`)
      }
    }

    throw new RemoteProtocolError("Florisoft command response ended without a Result message.")
  }

  private async tryPresent(message: RemoteMessage, signal?: AbortSignal, mode: "present" | "notify" = "present") {
    if (this.presenterResolver == null || typeof message.type !== "string" || message.type.trim().length === 0) {
      return { handled: false } as const
    }

    const resolution = this.presenterResolver.resolve(
      this.scopeIds,
      message.type,
      message.namespace,
      message.value,
      signal,
      { mode }
    )
    return resolution instanceof Promise ? await resolution : resolution
  }
}

export interface RemoteClient {
  readonly graphql: GraphQLClient
  readonly commands: CommandClient

  setAuthorization(authorization?: string): void
  getAuthorization(): string | undefined
  subscribeAuthorization(listener: (event: AuthorizationChangedEvent) => void): () => boolean
  subscribeAuthenticationRequired(listener: (event: AuthenticationRequiredEvent) => void): () => boolean
  setBasicCredentials(username: string, password: string): void
  setBaseUrl(baseUrl: string): void
  getBaseUrl(): string
}

const remoteClientTransports = new WeakMap<RemoteClient, RemoteTransport>()

class DefaultRemoteClient implements RemoteClient {
  readonly graphql: GraphQLClient
  readonly commands: CommandClient
  private readonly transport: RemoteTransport

  constructor(
    transport: RemoteTransport,
    fakeResolver?: RemoteFakeResolver,
    scopeIds: readonly string[] = [],
    presenterResolver?: PresenterResolver,
    telemetry?: ApplicationTelemetryRuntime,
    messenger?: Messenger
  ) {
    this.transport = transport
    remoteClientTransports.set(this, transport)
    this.graphql = new DefaultGraphQLClient(this.transport, fakeResolver, scopeIds, telemetry)
    this.commands = new DefaultCommandClient(
      this.transport,
      fakeResolver,
      scopeIds,
      presenterResolver,
      telemetry,
      messenger
    )
  }

  setAuthorization(authorization?: string) {
    this.transport.setAuthorization(authorization)
  }

  getAuthorization() {
    return this.transport.getAuthorization()
  }

  subscribeAuthorization(listener: (event: AuthorizationChangedEvent) => void) {
    return this.transport.subscribeAuthorization(listener)
  }

  subscribeAuthenticationRequired(listener: (event: AuthenticationRequiredEvent) => void) {
    return this.transport.subscribeAuthenticationRequired(listener)
  }

  setBasicCredentials(username: string, password: string) {
    this.transport.setBasicCredentials(username, password)
  }

  setBaseUrl(baseUrl: string) {
    this.transport.setBaseUrl(baseUrl)
  }

  getBaseUrl() {
    return this.transport.getBaseUrl()
  }
}

/** @internal */
export function getRemoteRevision(client: RemoteClient) {
  return remoteClientTransports.get(client)?.getRevision() ?? 0
}

/** @internal */
export function subscribeRemoteRevision(client: RemoteClient, listener: () => void) {
  return remoteClientTransports.get(client)?.subscribeRevision(listener) ?? (() => false)
}

/** @internal */
export function isRemoteAuthorizationSuspended(client: RemoteClient) {
  return remoteClientTransports.get(client)?.isAuthorizationSuspended() ?? false
}

/** @internal Withholds the authorization after a 401 without starting a new access context. */
export function suspendRemoteAuthorization(client: RemoteClient) {
  remoteClientTransports.get(client)?.suspendAuthorization()
}

/** @internal Resumes a suspended access context; false when there is nothing to resume. */
export function resumeRemoteAuthorization(client: RemoteClient, authorization: string) {
  return remoteClientTransports.get(client)?.resumeAuthorization(authorization) ?? false
}

/** @internal Notified when a suspended access context resumes, so work that failed with 401 can run again. */
export function subscribeRemoteAuthorizationResumed(client: RemoteClient, listener: () => void) {
  return remoteClientTransports.get(client)?.subscribeAuthorizationResumed(listener) ?? (() => undefined)
}

/** @internal True for the HTTP 401 that suspends the session's authorization. */
export function isRemoteAuthenticationFailure(error: unknown) {
  return error instanceof RemoteHttpError && error.status === 401
}

/** @internal */
export interface RemoteClientRuntime {
  readonly root: RemoteClient
  createScoped(scopeIds: readonly string[]): RemoteClient
}

/** @internal */
export function createRemoteClientRuntime(
  options: RemoteClientOptions,
  fakeResolver?: RemoteFakeResolver,
  presenterResolver?: PresenterResolver,
  telemetry?: ApplicationTelemetryRuntime,
  messenger?: Messenger
): RemoteClientRuntime {
  const transport = new RemoteTransport(options, telemetry)
  return Object.freeze({
    root: new DefaultRemoteClient(transport, fakeResolver, [], presenterResolver, telemetry, messenger),
    createScoped(scopeIds: readonly string[]) {
      return new DefaultRemoteClient(
        transport,
        fakeResolver,
        Object.freeze([...scopeIds]),
        presenterResolver,
        telemetry,
        messenger
      )
    },
  })
}

function isRemoteCancellation(error: unknown) {
  return error instanceof RemoteAccessContextChangedError || (error instanceof Error && error.name === "AbortError")
}

function logFakeExecution(
  telemetry: ApplicationTelemetryRuntime | undefined,
  kind: "query" | "command",
  name: string,
  durationMs: number,
  input: unknown,
  output: unknown
) {
  telemetry?.telemetry.debug(`Remote ${kind} handled by fake`, {
    "florisoft.remote.kind": kind,
    "florisoft.remote.name": name,
    "florisoft.remote.duration_ms": Math.max(0, durationMs),
    "florisoft.remote.fake": true,
    "florisoft.remote.input": serializeFakeTelemetryValue(input),
    "florisoft.remote.output": serializeFakeTelemetryValue(output),
  })
}

function serializeFakeTelemetryValue(value: unknown) {
  if (value === undefined) return "undefined"
  try {
    return JSON.stringify(value) ?? String(value)
  } catch {
    try {
      return String(value)
    } catch {
      return "[unserializable]"
    }
  }
}

function performanceNow() {
  return globalThis.performance?.now() ?? Date.now()
}
